import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchReuniones,
  fetchReunion,
  createReunion,
  updateReunion,
  deleteReunion,
  updateReunionEstado,
  fetchReunionLogs,
  createReunionAuto,
  reagendarReunion,
  fetchParticipantesReunion,
  agregarParticipanteReunion,
  eliminarParticipanteReunion,
  actualizarEstadoParticipante,
  agendarSiguienteReunion,
  obtenerCadenaReunion,
  obtenerHistorialServicio,
} from '../api/reuniones';
import {
  FilterReunionDto,
  CreateReunionDto,
  CreateReunionAutoDto,
  ReunionEstado,
  CreateParticipanteDto,
  AgendarSiguienteReunionDto,
  FilterHistorialReunionDto,
  Reunion,
} from '../types/reuniones';

const MEETINGS_KEY = 'reuniones';
const REGLAS_KEY = 'reunionReglas';
const PARTICIPANTES_KEY = 'reunionParticipantes';

// List meetings with filters
export function useReuniones(filters: FilterReunionDto | null = {}) {
  return useQuery({
    queryKey: [MEETINGS_KEY, filters],
    queryFn: () => fetchReuniones(filters ?? {}),
    enabled: filters !== null,
    staleTime: 0, // Siempre considerar los datos como obsoletos para forzar actualizaciones
    refetchOnWindowFocus: true, // Refetch cuando la ventana recupera el foco
    placeholderData: [],
  });
}

// Get single meeting
export function useReunion(id?: number) {
  return useQuery({
    queryKey: [MEETINGS_KEY, id],
    queryFn: () => fetchReunion(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

// Create manual meeting mutation
export function useCreateReunion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateReunionDto) => createReunion(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY] });
    },
  });
}

// Create auto-scheduled meeting mutation
export function useCreateReunionAuto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateReunionAutoDto) => createReunionAuto(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY] });
    },
  });
}

// Update meeting mutation
export function useUpdateReunion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<CreateReunionDto> }) =>
      updateReunion(id, payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY, variables.id] });
    },
  });
}

// Update meeting state mutation
export function useUpdateReunionEstado() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      estado,
      descripcion,
    }: {
      id: number;
      estado: ReunionEstado;
      descripcion?: string;
    }) => updateReunionEstado(id, estado, descripcion),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY, variables.id] });
    },
  });
}

// Delete meeting mutation
export function useDeleteReunion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteReunion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY] });
    },
  });
}

// Get meeting logs
export function useReunionLogs(id?: number) {
  return useQuery({
    queryKey: ['reunionLogs', id],
    queryFn: () => fetchReunionLogs(id!),
    enabled: !!id,
  });
}

// Helper to get meeting color by type
export function getReunionColor(tipo: string): string {
  const colors: Record<string, string> = {
    bienvenida: '#4CAF50', // Green
    seguimiento: '#2196F3', // Blue
    incidencia: '#F44336', // Red
    otro: '#9C27B0', // Purple
  };
  return colors[tipo] || '#757575';
}

// Helper to get meeting color by status
export function getReunionStatusColor(estado: string): string {
  const colors: Record<string, string> = {
    programada: '#FF9800', // Orange
    realizada: '#4CAF50', // Green
    no_show_familia: '#F44336', // Red
    no_show_staff: '#F44336', // Red
    cancelada_familia: '#9E9E9E', // Gray
    cancelada_staff: '#9E9E9E', // Gray
    cancelada_sistema: '#9E9E9E', // Gray
  };
  return colors[estado] || '#757575';
}

/* ══════════════════════════════════════════════════════════════
   HELPERS PARA REUNIONES EN CADENA
 ══════════════════════════════════════════════════════════════ */

/**
 * IDs de servicios que tienen seguimiento recurrente
 */
export const SERVICIOS_RECURRENTES = {
  APOYO_PSICOLOGICO: 5,
  COUNSELING: 6,
} as const;

/**
 * Verifica si una reunión es de Apoyo Psicológico o Counseling
 * @param reunion Reunión a verificar
 * @returns true si es de Apoyo/Counseling, false en caso contrario
 */
export function esReunionApoyoOCounseling(reunion: Reunion | null | undefined): boolean {
  if (!reunion || !reunion.servicioId) return false;
  return (
    reunion.servicioId === SERVICIOS_RECURRENTES.APOYO_PSICOLOGICO ||
    reunion.servicioId === SERVICIOS_RECURRENTES.COUNSELING
  );
}

/**
 * Obtiene el nombre del servicio por su ID
 * @param servicioId ID del servicio
 * @returns Nombre del servicio o 'Servicio desconocido'
 */
export function getServicioNombre(servicioId: number | null | undefined): string {
  if (!servicioId) return 'Servicio desconocido';
  switch (servicioId) {
    case SERVICIOS_RECURRENTES.APOYO_PSICOLOGICO:
      return 'Seguimiento psicopedagógico';
    case SERVICIOS_RECURRENTES.COUNSELING:
      return 'Counselling Universitario y Profesional';
    default:
      return 'Servicio desconocido';
  }
}
/* ══════════════════════════════════════════════════════════════
   HOOKS PARA PARTICIPANTES
 ══════════════════════════════════════════════════════════════ */

export function useParticipantesReunion(idReunion?: number) {
  return useQuery({
    queryKey: [PARTICIPANTES_KEY, idReunion],
    queryFn: () => fetchParticipantesReunion(idReunion!),
    enabled: !!idReunion,
    staleTime: 1000 * 60 * 5,
  });
}

export function useAgregarParticipante() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ idReunion, payload }: { idReunion: number; payload: CreateParticipanteDto }) =>
      agregarParticipanteReunion(idReunion, payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PARTICIPANTES_KEY, variables.idReunion] });
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY, variables.idReunion] });
    },
  });
}

export function useEliminarParticipante() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ idReunion, idUsuario }: { idReunion: number; idUsuario: number }) =>
      eliminarParticipanteReunion(idReunion, idUsuario),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PARTICIPANTES_KEY, variables.idReunion] });
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY, variables.idReunion] });
    },
  });
}

export function useActualizarEstadoParticipante() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      idReunion,
      idUsuario,
      estado,
    }: {
      idReunion: number;
      idUsuario: number;
      estado: string;
    }) => actualizarEstadoParticipante(idReunion, idUsuario, estado),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PARTICIPANTES_KEY, variables.idReunion] });
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY, variables.idReunion] });
    },
  });
}

/* ══════════════════════════════════════════════════════════════
   HOOKS PARA REUNIONES EN CADENA
 ══════════════════════════════════════════════════════════════ */

// Schedule next meeting in chain
export function useAgendarSiguienteReunion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: AgendarSiguienteReunionDto }) =>
      agendarSiguienteReunion(id, payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY, variables.id] });
      queryClient.invalidateQueries({ queryKey: ['reunionCadena', variables.id] });
    },
  });
}

// Reagendar reunión (automático)
export function useReagendarReunion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => reagendarReunion(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [MEETINGS_KEY, id] });
      queryClient.invalidateQueries({ queryKey: ['reunionCadena', id] });
    },
  });
}

// Get meeting chain
export function useCadenaReunion(id?: number) {
  return useQuery({
    queryKey: ['reunionCadena', id],
    queryFn: () => obtenerCadenaReunion(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

// Get meeting history by service
export function useHistorialServicio(filters: FilterHistorialReunionDto) {
  return useQuery({
    queryKey: ['reunionHistorial', filters],
    queryFn: () => obtenerHistorialServicio(filters),
    enabled: !!filters.alumnoId && !!filters.servicioId,
    staleTime: 1000 * 60 * 5,
  });
}
